import { Injectable } from '@angular/core';
import type { CommentaryCreateInterface } from '@zaoblako/shared/modules/messenger/dtos/commentary/commentary-create.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import type { ReminderFormInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder.form.dto';
import type { ChecklistFormInterface } from '@zaoblako/shared/modules/project/dto/checklist/checklist-form.dto';
import type { ChecklistFindInterface } from '@zaoblako/shared/modules/project/dto/checklist/checklist-find.dto';
import type { DeadlineDelayRequestFindInterface } from '@zaoblako/shared/modules/project/dto/deadline-delay-request/deadline-delay-request-find.dto';
import type { DeadlineDelayRequestFormInterface } from '@zaoblako/shared/modules/project/dto/deadline-delay-request/deadline-delay-request-form.dto';
import type { HistoryFindInterface } from '@zaoblako/shared/common/dtos/history/history-find.dto';
import type { ProjectTimeReportQueryInterface } from '@zaoblako/shared/modules/project/dto/project-time-report/project-time-report-query.dto';
import type { ProjectTimeReportResultInterface } from '@zaoblako/shared/modules/project/dto/project-time-report/project-time-report-result.dto';
import type { ProjectAddFilesInterface } from '@zaoblako/shared/modules/project/dto/project/project-add-files.dto';
import type { ProjectDealAssignInterface } from '@zaoblako/shared/modules/project/dto/project/project-deal-assign.dto';
import type { ProjectDealQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-deal-query.dto';
import type { FactMinInterface } from '@zaoblako/shared/common/dtos/fact-min.dto';
import type { ProjectHelpdeskTasksFormInterface } from '@zaoblako/shared/modules/project/dto/project/project-helpdesk-tasks-form.dto';
import type { ProjectHelpdeskTasksQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-helpdesk-tasks-query.dto';
import type { ProjectListQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-list-query.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { ProjectSimplifiedListQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-simplified-list-query.dto';
import type { ProjectWorkflowActionResultInterface } from '@zaoblako/shared/modules/project/dto/project/project-workflow-action-result.dto';
import type { ProjectPageInterface } from '@zaoblako/shared/modules/project/dto/project/project-page.dto';
import type { SubprojectDefaultsFindInterface } from '@zaoblako/shared/modules/project/dto/subproject-defaults/subproject-defaults-find.dto';
import type { SubprojectDefaultsFormInterface } from '@zaoblako/shared/modules/project/dto/subproject-defaults/subproject-defaults-form.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { ProjectAccessibleQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-accessible-query.dto';
import type { ProjectFileFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-file-find.dto';
import type { NotificationSubscriptionStatusInterface } from '@zaoblako/shared/modules/notification/dtos/notification-subscription-status.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProjectService extends BaseService {
  protected baseUrl = '/project';

  public search(queryParams?: ProjectSimplifiedListQueryInterface): Observable<ProjectFindInterface[]> {
    return this.http.get<ProjectFindInterface[]>(`${this.baseUrl}/list/simplified`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public hierarchy(queryParams: ProjectListQueryInterface): Observable<ProjectPageInterface> {
    return this.http.get<ProjectPageInterface>(`${this.baseUrl}/list/hierarchy`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public flat(queryParams: ProjectListQueryInterface): Observable<ProjectPageInterface> {
    return this.http.get<ProjectPageInterface>(`${this.baseUrl}/list/flat`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getAccessibleProjects(queryParams: ProjectAccessibleQueryInterface): Observable<ProjectFindInterface[]> {
    return this.http.get<ProjectFindInterface[]>(`${this.baseUrl}/list/accessible`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public createChecklist(projectId: number, body: ChecklistFormInterface): Observable<ChecklistFindInterface> {
    return this.http.post<ChecklistFindInterface>(`${this.baseUrl}/${projectId}/checklist`, body);
  }

  public getMembers(projectId: number): Observable<UserFindInterface[]> {
    return this.http.get<UserFindInterface[]>(`${this.baseUrl}/${projectId}/participants`);
  }

  public toggleFavorite(projectId: number): Observable<boolean> {
    return this.http.put<boolean>(`${this.baseUrl}/${projectId}/favorite`, {});
  }

  public subprojectHierarchy(projectId: number, queryParams: ProjectListQueryInterface): Observable<ProjectPageInterface> {
    return this.http.get<ProjectPageInterface>(`${this.baseUrl}/${projectId}/children/hierarchy`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public subprojectFlat(projectId: number, queryParams: ProjectListQueryInterface): Observable<ProjectPageInterface> {
    return this.http.get<ProjectPageInterface>(`${this.baseUrl}/${projectId}/children/flat`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getHistory(projectId: number): Observable<HistoryFindInterface[]> {
    return this.http.get<HistoryFindInterface[]>(`${this.baseUrl}/${projectId}/history`);
  }

  public createReminder(projectId: number, params: ReminderFormInterface): Observable<ReminderFindInterface> {
    return this.http.post<ReminderFindInterface>(`${this.baseUrl}/${projectId}/reminder`, params);
  }

  public sendChangeDeadline(
    projectId: number,
    params: DeadlineDelayRequestFormInterface
  ): Observable<DeadlineDelayRequestFindInterface> {
    return this.http.post<DeadlineDelayRequestFindInterface>(`${this.baseUrl}/${projectId}/deadline-delay-request`, params);
  }

  public refuseDeadline(projectId: number): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/deadline-delay-request/refuse`, {});
  }

  public acceptDeadline(projectId: number, params: DeadlineDelayRequestFormInterface): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/deadline-delay-request/accept`, params);
  }

  public removeReminder(projectId: number): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${projectId}/reminder`);
  }

  public changeStatus(projectId: number, type: 'FINISH' | 'ACCEPT' | 'REJECT'): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/${type}`, {});
  }

  public getFiles(projectId: number): Observable<ProjectFileFindInterface[]> {
    return this.http.get<ProjectFileFindInterface[]>(`${this.baseUrl}/${projectId}/files`);
  }

  public addFiles(projectId: number, body: ProjectAddFilesInterface): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/files`, body);
  }

  public assign(projectId: number): Observable<ProjectWorkflowActionResultInterface> {
    return this.http.post<ProjectWorkflowActionResultInterface>(`${this.baseUrl}/${projectId}/workflow/assign`, {});
  }

  public takeToWork(projectId: number): Observable<ProjectWorkflowActionResultInterface> {
    return this.http.post<ProjectWorkflowActionResultInterface>(`${this.baseUrl}/${projectId}/workflow/take-to-work`, {});
  }

  public submitForReview(projectId: number): Observable<ProjectWorkflowActionResultInterface> {
    return this.http.post<ProjectWorkflowActionResultInterface>(
      `${this.baseUrl}/${projectId}/workflow/submit-for-review`,
      {}
    );
  }

  public accept(projectId: number): Observable<ProjectWorkflowActionResultInterface> {
    return this.http.post<ProjectWorkflowActionResultInterface>(`${this.baseUrl}/${projectId}/workflow/accept`, {});
  }

  public bringBackToWork(projectId: number): Observable<ProjectWorkflowActionResultInterface> {
    return this.http.post<ProjectWorkflowActionResultInterface>(
      `${this.baseUrl}/${projectId}/workflow/bring-back-to-work`,
      {}
    );
  }

  public pause(projectId: number, comment: CommentaryCreateInterface): Observable<ProjectWorkflowActionResultInterface> {
    return this.http.post<ProjectWorkflowActionResultInterface>(`${this.baseUrl}/${projectId}/workflow/pause`, comment);
  }

  public cancel(projectId: number, comment: CommentaryCreateInterface): Observable<ProjectWorkflowActionResultInterface> {
    return this.http.post<ProjectWorkflowActionResultInterface>(`${this.baseUrl}/${projectId}/workflow/cancel`, comment);
  }

  public convertToTheme(projectId: number): Observable<ProjectWorkflowActionResultInterface> {
    return this.http.post<ProjectWorkflowActionResultInterface>(`${this.baseUrl}/${projectId}/workflow/convert-to-theme`, {});
  }

  public breadcrumbs(projectId: number): Observable<ProjectFindInterface[]> {
    return this.http.get<ProjectFindInterface[]>(`${this.baseUrl}/${projectId}/breadcrumbs`);
  }

  public getProjectTimeReport(queryParams: ProjectTimeReportQueryInterface): Observable<ProjectTimeReportResultInterface> {
    return this.http.get<ProjectTimeReportResultInterface>(`${this.baseUrl}/report/time`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getProjectTimeSums(
    queryParams: ProjectTimeReportQueryInterface
  ): Observable<Omit<ProjectTimeReportResultInterface, 'items'>> {
    return this.http.get<Omit<ProjectTimeReportResultInterface, 'items'>>(`${this.baseUrl}/report/time/sums`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getProjectTimeReportAsExcel(queryParams: ProjectTimeReportQueryInterface): Observable<BlobPart> {
    return this.http.get<BlobPart>(`${this.baseUrl}/report/time/excel`, {
      params: this.objectToHttpParams(queryParams),
      responseType: 'arraybuffer' as any
    });
  }

  public getHelpdeskTasks(
    projectId: number,
    queryParams: ProjectHelpdeskTasksQueryInterface
  ): Observable<HelpdeskTaskFindInterface[]> {
    return this.http.get<HelpdeskTaskFindInterface[]>(`${this.baseUrl}/${projectId}/helpdesk-tasks`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public setHelpdeskTasks(projectId: number, body: ProjectHelpdeskTasksFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${projectId}/helpdesk-tasks`, body);
  }

  public getDeals(projectId: number, queryParams: ProjectDealQueryInterface): Observable<DealFindInterface[]> {
    return this.http.get<DealFindInterface[]>(`${this.baseUrl}/${projectId}/deals`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public setDeals(projectId: number, body: ProjectDealAssignInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${projectId}/deals`, body);
  }

  public getFactTime(id: number): Observable<FactMinInterface> {
    return this.http.get<FactMinInterface>(`${this.baseUrl}/${id}/fact-time`);
  }

  public getSubprojectDefaults(id: number): Observable<SubprojectDefaultsFindInterface> {
    return this.http.get<SubprojectDefaultsFindInterface>(`${this.baseUrl}/${id}/subproject-defaults`);
  }

  public updateSubprojectDefaults(id: number, body: SubprojectDefaultsFormInterface): Observable<SubprojectDefaultsFindInterface> {
    return this.http.put<SubprojectDefaultsFindInterface>(`${this.baseUrl}/${id}/subproject-defaults`, body);
  }

  public getNotificationSubscriptionStatus(id: number): Observable<NotificationSubscriptionStatusInterface> {
    return this.http.get<NotificationSubscriptionStatusInterface>(`${this.baseUrl}/${id}/notification`);
  }

  public subscribeToNotifications(id: number): Observable<NotificationSubscriptionStatusInterface> {
    return this.http.put<NotificationSubscriptionStatusInterface>(`${this.baseUrl}/${id}/notification`, null);
  }

  public unsubscribeFromNotifications(id: number): Observable<NotificationSubscriptionStatusInterface> {
    return this.http.delete<NotificationSubscriptionStatusInterface>(`${this.baseUrl}/${id}/notification`);
  }
}
